if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");

        const variantInput = this.variantIdInput;
        if (variantInput) {
          variantInput.disabled = false;
        }

        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));

        // Prioritize cart-drawer over cart-notification
        this.cart =
          document.querySelector("cart-drawer") ||
          document.querySelector("cart-notification");

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton?.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
        this.defaultSubmitCooldownMs = 1500;
        this.submitCooldownUntil = 0;
        this.submitCooldownTimer = null;
      }

      hasActiveSubmitCooldown() {
        return Date.now() < this.submitCooldownUntil;
      }

      releaseSubmitButtonWithCooldown(durationMs = 0) {
        if (!this.submitButton) return;

        window.clearTimeout(this.submitCooldownTimer);

        const cooldownMs = Math.max(0, durationMs);
        if (cooldownMs === 0) {
          this.submitCooldownUntil = 0;
          this.submitButton.removeAttribute("aria-disabled");
          return;
        }

        this.submitCooldownUntil = Date.now() + cooldownMs;
        this.submitButton.setAttribute("aria-disabled", true);

        this.submitCooldownTimer = window.setTimeout(() => {
          this.submitCooldownUntil = 0;
          if (!this.submitButton.classList.contains("loading")) {
            this.submitButton.removeAttribute("aria-disabled");
          }
        }, cooldownMs);
      }

      reloadAfterChallenge() {
        window.location.reload();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        let isModalSubmit = false;
        if (this.form.dataset.isModalSubmit === "true") {
          isModalSubmit = true;
        } else {
          let parent = this.form.parentElement;
          while (parent) {
            if (
              parent.tagName === "MODAL-DIALOG" &&
              parent.id.startsWith("StickyVariantModal-")
            ) {
              isModalSubmit = true;
              break;
            }
            parent = parent.parentElement;
          }
        }

        const context = this.closest("product-info") || document;

        const sizePicker = context.querySelector(".size-picker");
        const hasSizePicker = sizePicker !== null;
        const selectedSize = hasSizePicker
          ? sizePicker.querySelector(
              'input[type="radio"]:checked:not(.toggle-radio)'
            )
          : null;

        const lengthPicker = context.querySelector(".length-picker");
        const hasLengthPicker = lengthPicker !== null;
        const selectedLength = hasLengthPicker
          ? lengthPicker.querySelector('input[type="radio"]:checked')
          : null;

        const sizeErrorEl = context.querySelector(
          ".product-form__error-message-wrapper-variant"
        );

        if (hasSizePicker && !selectedSize) {
          if (sizeErrorEl) {
            sizeErrorEl.style.display = "block";
            const span = sizeErrorEl.querySelector("span");
            if (span) span.textContent = "Select Size";
          }
          this.submitButton.removeAttribute("aria-disabled");
          const spinner = this.querySelector(".loading__spinner");
          if (spinner) spinner.classList.add("hidden");
          return;
        }

        if (hasLengthPicker && !selectedLength) {
          if (sizeErrorEl) {
            sizeErrorEl.style.display = "block";
            const span = sizeErrorEl.querySelector("span");
            if (span) span.textContent = "Select Length";
          }
          this.submitButton.removeAttribute("aria-disabled");
          const spinner = this.querySelector(".loading__spinner");
          if (spinner) spinner.classList.add("hidden");
          return;
        }

        if (
          this.submitButton.getAttribute("aria-disabled") === "true" ||
          this.hasActiveSubmitCooldown()
        ) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        const spinner = this.querySelector(".loading__spinner");
        if (spinner) spinner.classList.remove("hidden");

        const baseFormData = new FormData(this.form);
        const quantitySelect =
          this.form.querySelector('select[name="quantity"]') ||
          document.querySelector('select[name="quantity"]');

        let wasDisabled = false;
        if (quantitySelect && quantitySelect.disabled) {
          quantitySelect.disabled = false;
          wasDisabled = true;
        }

        if (wasDisabled && quantitySelect) {
          quantitySelect.disabled = true;
        }

        let quantityRaw = baseFormData.get("quantity");

        if (quantityRaw === null && quantitySelect) {
          quantityRaw = quantitySelect.value;
        }

        let quantity = parseInt(quantityRaw, 10);
        if (isNaN(quantity) || quantity <= 0) {
          quantity = 1;
        }

        const sections =
          typeof this.cart?.getSectionsToRender === "function"
            ? this.cart.getSectionsToRender().map((section) => section.id)
            : ["cart-drawer", "cart-icon-bubble"];

        const getLocalePrefix = () => {
          const path = window.location.pathname;
          const match = path.match(/^\/([a-zA-Z]{2}(?:-[a-zA-Z]{2})?)/);
          return match ? "/" + match[1] : "";
        };

        const getCartUrl = () => {
          return window.routes?.cart_url || `${getLocalePrefix()}/cart`;
        };

        const ensureAjaxEndpoint = (url) => {
          return url.endsWith(".js") ? url : `${url}.js`;
        };

        const getCartAddUrl = () => {
          return ensureAjaxEndpoint(
            window.routes?.cart_add_url || `${getCartUrl()}/add`
          );
        };

        const getCartJsonUrl = () => {
          return ensureAjaxEndpoint(getCartUrl());
        };

        const getCartLinkWithParams = (params) => {
          const query = params.toString();
          return query ? `${getCartUrl()}?${query}` : getCartUrl();
        };

        const parseSectionInnerHtml = (html, selector = ".shopify-section") => {
          return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
        };

        const fetchCartData = async () => {
          const cartRes = await fetch(getCartJsonUrl());
          if (!cartRes.ok) {
            throw new Error(`Cart fetch failed (HTTP ${cartRes.status})`);
          }

          const cartData = await cartRes.json();
          sessionStorage.setItem("cart_trial", JSON.stringify(cartData));
          return cartData;
        };

        const addItems = async (requestedQuantity) => {
          const formData = new FormData();
          for (let [key, value] of baseFormData.entries()) {
            if (key !== "quantity") formData.append(key, value);
          }

          formData.append("quantity", requestedQuantity);
          formData.append("sections", sections.join(","));
          formData.append("sections_url", window.location.pathname);
          formData.append("properties[_unique_id]", crypto.randomUUID());

          const res = await fetch(getCartAddUrl(), {
            method: "POST",
            body: formData,
            headers: {
              "X-Requested-With": "XMLHttpRequest",
            },
          });

          if (!res.ok) {
            let errorDescription = `Add to cart failed (HTTP ${res.status})`;
            const contentType = res.headers.get("content-type") || "";
            const isHtmlResponse = contentType.includes("text/html");

            if (!isHtmlResponse) {
              try {
                const errorData = await res.json();
                errorDescription = errorData?.description || errorDescription;
              } catch (parseError) {
                console.error("Failed to parse add to cart error:", parseError);
              }
            } else if (res.status === 429) {
              errorDescription =
                "We need to verify your connection before adding this item. Please refresh the page and try again.";
            }

            const error = new Error(errorDescription);
            error.statusCode = res.status;
            error.isChallengeResponse = res.status === 429 && isHtmlResponse;
            throw error;
          }

          return res.json();
        };

        const renderCartContents = async (addResponse, cartData) => {
          const responseSections = addResponse?.sections || {};
          const cartUrl = getCartUrl();

          if (window.innerWidth <= 1024 || isModalSubmit) {
            const notification = document.getElementById(
              "AddedToBagNotification"
            );

            // Detect ?app-view=true
            const urlParams = new URLSearchParams(window.location.search);
            const isAppView = urlParams.get("app-view") === "true";

            if (isAppView) {
              // Remove existing modal if any
              if (notification) notification.classList.add("hidden");

              // Create the app-view style flyout
              const existingFlyout = document.querySelector(
                ".app-view-add-to-bag-flyout"
              );
              if (existingFlyout) existingFlyout.remove();

              const flyout = document.createElement("div");
              flyout.className = "app-view-add-to-bag-flyout";
              flyout.innerHTML = `
      <div class="flyout-text">Item Added</div>
      <a href="${getCartLinkWithParams(urlParams)}" class="flyout-btn">VIEW BAG </a>
    `;
              document.body.appendChild(flyout);

              // Auto-hide after 3s
              setTimeout(() => {
                flyout.remove();
              }, 3000);
            } else {
              // Default existing modal behavior
              if (notification) {
                notification.classList.remove("hidden");
                setTimeout(() => {
                  notification.classList.add("hidden");
                }, 3000);
              }
            }

            try {
              const bubble = document.querySelector("#cart-icon-bubble");
              if (bubble && responseSections["cart-icon-bubble"]) {
                const parsedBubble = parseSectionInnerHtml(
                  responseSections["cart-icon-bubble"]
                );
                if (parsedBubble) {
                  bubble.innerHTML = parsedBubble.innerHTML;
                }
              }
            } catch (e) {
              console.error("Error updating cart bubble:", e);
            }

            return;
          }

          if (this.cart && typeof this.cart.renderContents === "function") {
            try {
              this.cart.renderContents({
                items: cartData.items,
                sections: responseSections,
                total_price: cartData.total_price,
              });
            } catch (e) {
              console.error("Error rendering cart contents:", e);
              if (!isModalSubmit) {
                window.location.href = cartUrl;
              }
            }
          }

          if (
            this.cart &&
            this.cart.tagName.toLowerCase() === "cart-drawer" &&
            typeof this.cart.open === "function"
          ) {
            try {
              this.cart.open(this.submitButton);
              this.cart.classList.add("animate", "active");
              this.cart.style.display = "block";
              this.cart.style.visibility = "visible";
            } catch (e) {
              console.error("Error opening cart drawer:", e);
              if (!isModalSubmit) {
                window.location.href = cartUrl;
              }
            }
          } else {
            try {
              const bubble = document.querySelector("#cart-icon-bubble");
              if (bubble && responseSections["cart-icon-bubble"]) {
                bubble.dispatchEvent(new Event("click", { bubbles: true }));
              } else {
                if (!isModalSubmit) {
                  window.location.href = cartUrl;
                }
              }
            } catch (e) {
              console.error("Error triggering cart bubble:", e);
              if (!isModalSubmit) {
                window.location.href = cartUrl;
              }
            }
          }
        };

        (async () => {
          let isChallengeReload = false;

          try {
            const addResponse = await addItems(quantity);
            const needsCartData =
              window?.customer?.id || !(window.innerWidth <= 1024 || isModalSubmit);
            const cartData = needsCartData ? await fetchCartData() : null;

            try {
              await updateCustomerCartMetafield(cartData);
            } catch (metaError) {
              console.warn(
                "updateCustomerCartMetafield failed after add to bag:",
                metaError
              );
            }

            const quickAddModal = this.closest("quick-add-modal");
            const stickyVariantModal = isModalSubmit
              ? this.closest('modal-dialog[id^="StickyVariantModal-"]')
              : null;

            if (quickAddModal || stickyVariantModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => renderCartContents(addResponse, cartData)
                    );
                  });
                },
                { once: true }
              );

              if (quickAddModal) {
                quickAddModal.hide(true);
              } else if (stickyVariantModal) {
                stickyVariantModal.hide(true);
              }
            } else {
              CartPerformance.measure(
                "add:paint-updated-sections",
                () => renderCartContents(addResponse, cartData)
              );
            }
          } catch (error) {
            if (error?.isChallengeResponse) {
              isChallengeReload = true;
              this.reloadAfterChallenge();
            } else {
              this.handleErrorMessage(error.message || "Failed to add item.");
            }
            document.body.classList.remove("overflow-hidden");
          } finally {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty")) {
              this.cart.classList.remove("is-empty");
            }
            const spinner = this.querySelector(".loading__spinner");
            if (spinner) spinner.classList.add("hidden");
            if (!isChallengeReload) {
              this.releaseSubmitButtonWithCooldown(this.defaultSubmitCooldownMs);
            }
            CartPerformance.measureFromEvent("add:user-action", evt);
          }
        })();
      }

      // onSubmitHandler(evt) {
      //   evt.preventDefault();
      //   if (this.submitButton.getAttribute("aria-disabled") === "true") return;

      //   this.handleErrorMessage();

      //   this.submitButton.setAttribute("aria-disabled", true);
      //   this.submitButton.classList.add("loading");
      //   this.querySelector(".loading__spinner").classList.remove("hidden");

      //   const config = fetchConfig("javascript");
      //   config.headers["X-Requested-With"] = "XMLHttpRequest";
      //   delete config.headers["Content-Type"];

      //   // const formData = new FormData(this.form);
      //   const baseFormData = new FormData(this.form);
      //   const quantity = parseInt(baseFormData.get("quantity") || 1);

      //   const sections =
      //     typeof this.cart?.getSectionsToRender === "function"
      //       ? this.cart.getSectionsToRender().map((section) => section.id)
      //       : ["cart-drawer", "cart-icon-bubble"];

      //   const addSingleItem = async () => {
      //     const formData = new FormData();
      //     for (let [key, value] of baseFormData.entries()) {
      //       if (key !== "quantity") formData.append(key, value);
      //     }

      //     formData.append("quantity", 1);
      //     formData.append("sections", sections);
      //     formData.append("sections_url", window.location.pathname);
      //     formData.append("properties[_unique_id]", crypto.randomUUID());

      //     const res = await fetch("/cart/add.js", {
      //       method: "POST",
      //       body: formData,
      //       headers: {
      //         "X-Requested-With": "XMLHttpRequest",
      //       },
      //     });

      //     if (!res.ok) {
      //       const errorData = await res.json();
      //       throw new Error(errorData?.description || "Add to cart failed");
      //     }

      //     return res.json();
      //   };

      //   const quickAddModal = this.closest("quick-add-modal");

      //   const renderCartContents = async () => {
      //     const cartData = await fetch("/cart.js").then((res) => res.json());

      //     if (this.cart && typeof this.cart.renderContents === "function") {
      //       try {
      //         this.cart.renderContents(cartData);
      //       } catch (e) {}
      //     } else {
      //       window.location.href = "/cart";
      //     }
      //   };

      //   (async () => {
      //     try {
      //       for (let i = 0; i < quantity; i++) {
      //         await addSingleItem();
      //       }
      //       window.location.reload();

      //       if (quickAddModal) {
      //         document.body.addEventListener(
      //           "modalClosed",
      //           () => {
      //             setTimeout(() => {
      //               CartPerformance.measure(
      //                 "add:paint-updated-sections",
      //                 renderCartContents
      //               );
      //             });
      //           },
      //           { once: true }
      //         );
      //         quickAddModal.hide(true);
      //       } else {
      //         CartPerformance.measure(
      //           "add:paint-updated-sections",
      //           renderCartContents
      //         );
      //       }
      //     } catch (error) {
      //       this.handleErrorMessage(error.message || "Failed to add item.");
      //       document.body.classList.remove("overflow-hidden");
      //     } finally {
      //       this.submitButton.classList.remove("loading");
      //       if (this.cart && this.cart.classList.contains("is-empty")) {
      //         this.cart.classList.remove("is-empty");
      //       }
      //       this.submitButton.removeAttribute("aria-disabled");
      //       this.querySelector(".loading__spinner").classList.add("hidden");

      //       CartPerformance.measureFromEvent("add:user-action", evt);
      //     }
      //   })();
      //   if (this.cart) {
      //     // Fallback to default sections if getSectionsToRender is not available
      //     const sections =
      //       typeof this.cart.getSectionsToRender === "function"
      //         ? this.cart.getSectionsToRender().map((section) => section.id)
      //         : ["cart-drawer", "cart-icon-bubble"];
      //     formData.append("sections", sections);
      //     formData.append("sections_url", window.location.pathname);
      //     this.cart.setActiveElement(document.activeElement);
      //   }
      //   config.body = formData;

      //   fetch(`${routes.cart_add_url}`, config)
      //     .then((response) => response.json())
      //     .then((response) => {
      //       if (response.status) {
      //         publish(PUB_SUB_EVENTS.cartError, {
      //           source: "product-form",
      //           productVariantId: formData.get("id"),
      //           errors: response.errors || response.description,
      //           message: response.message,
      //         });
      //         this.handleErrorMessage(response.description);

      //         const soldOutMessage =
      //           this.submitButton.querySelector(".sold-out-message");
      //         if (!soldOutMessage) return;
      //         this.submitButton.setAttribute("aria-disabled", true);
      //         this.submitButtonText.classList.add("hidden");
      //         soldOutMessage.classList.remove("hidden");
      //         this.error = true;
      //         return;
      //       }

      //       const startMarker = CartPerformance.createStartingMarker(
      //         "add:wait-for-subscribers"
      //       );
      //       if (!this.error)
      //         publish(PUB_SUB_EVENTS.cartUpdate, {
      //           source: "product-form",
      //           productVariantId: formData.get("id"),
      //           cartData: response,
      //         }).then(() => {
      //           CartPerformance.measureFromMarker(
      //             "add:wait-for-subscribers",
      //             startMarker
      //           );
      //         });
      //       this.error = false;

      //       const quickAddModal = this.closest("quick-add-modal");
      //       const renderCartContents = () => {
      //         // Open cart drawer first
      //         if (
      //           this.cart &&
      //           this.cart.tagName.toLowerCase() === "cart-drawer" &&
      //           typeof this.cart.open === "function"
      //         ) {
      //           try {
      //             this.cart.open(this.submitButton);
      //             // Ensure visibility
      //             this.cart.classList.add("animate", "active");
      //             this.cart.style.display = "block";
      //             this.cart.style.visibility = "visible";
      //             const cartDrawerInner =
      //               this.cart.querySelector("#CartDrawer");
      //             if (cartDrawerInner) {
      //               cartDrawerInner.style.transform = "translateX(0)";
      //               cartDrawerInner.style.right = "0";
      //             }
      //             const cartInnerContent = this.cart.querySelector(
      //               ".cart-drawer__inner"
      //             );
      //             if (cartInnerContent) {
      //               cartInnerContent.style.display = "flex";
      //               cartInnerContent.style.visibility = "visible";
      //               cartInnerContent.style.position = "relative";
      //               cartInnerContent.style.right = "0";
      //             }
      //             const overlay = this.cart.querySelector(
      //               "#CartDrawer-Overlay"
      //             );
      //             if (overlay) {
      //               overlay.classList.add("active");
      //               overlay.style.display = "block";
      //               overlay.style.opacity = "1";
      //               overlay.style.zIndex = "49";
      //             } else {
      //               document.body.classList.remove("overflow-hidden");
      //             }
      //           } catch (e) {
      //             document.body.classList.remove("overflow-hidden");
      //             const cartIcon = document.querySelector("#cart-icon-bubble");
      //             if (cartIcon) {
      //               cartIcon.dispatchEvent(
      //                 new Event("click", { bubbles: true })
      //               );
      //             } else {
      //               document.body.classList.remove("overflow-hidden");
      //             }
      //           }
      //         } else {
      //           document.body.classList.remove("overflow-hidden");
      //           const cartIcon = document.querySelector("#cart-icon-bubble");
      //           if (cartIcon) {
      //             cartIcon.dispatchEvent(new Event("click", { bubbles: true }));
      //           }
      //         }
      //         // Update cart contents
      //         if (
      //           this.cart &&
      //           typeof this.cart.renderContents === "function" &&
      //           response.items
      //         ) {
      //           try {
      //             this.cart.renderContents(response);
      //           } catch (e) {}
      //         }
      //         // Fallback: Try rendering via cart-items
      //         if (
      //           this.cart &&
      //           this.cart.tagName.toLowerCase() === "cart-drawer"
      //         ) {
      //           const cartItems = this.cart.querySelector("cart-items");
      //           if (
      //             cartItems &&
      //             typeof cartItems.renderContents === "function" &&
      //             response.items
      //           ) {
      //             try {
      //               cartItems.renderContents(response);
      //             } catch (e) {}
      //           }
      //           // Fallback: Manually update cart-drawer HTML
      //           if (response.sections && response.sections["cart-drawer"]) {
      //             try {
      //               const drawerInner =
      //                 this.cart.querySelector(".drawer__inner") || this.cart;
      //               if (drawerInner) {
      //                 drawerInner.innerHTML = response.sections["cart-drawer"];
      //                 if (
      //                   drawerInner.classList.contains("cart-drawer__inner")
      //                 ) {
      //                   drawerInner.style.display = "flex";
      //                   drawerInner.style.visibility = "visible";
      //                   drawerInner.style.position = "relative";
      //                   drawerInner.style.right = "0";
      //                   drawerInner.style.opacity = "1";
      //                 }
      //               } else {
      //                 document.body.classList.remove("overflow-hidden");
      //               }
      //             } catch (e) {
      //               document.body.classList.remove("overflow-hidden");
      //             }
      //           } else {
      //             document.body.classList.remove("overflow-hidden");
      //           }
      //         }
      //         // Update cart-icon-bubble
      //         if (response.sections && response.sections["cart-icon-bubble"]) {
      //           try {
      //             const bubble = document.querySelector("#cart-icon-bubble");
      //             if (bubble) {
      //               bubble.innerHTML = response.sections["cart-icon-bubble"];
      //             }
      //           } catch (e) {}
      //         }
      //         // Check visibility after update
      //         setTimeout(() => {
      //           const cartInnerContent = this.cart.querySelector(
      //             ".cart-drawer__inner"
      //           );
      //           const cartDrawerInner = this.cart.querySelector("#CartDrawer");
      //           const isVisible =
      //             this.cart.classList.contains("active") &&
      //             window.getComputedStyle(this.cart).visibility === "visible" &&
      //             cartInnerContent &&
      //             window.getComputedStyle(cartInnerContent).display ===
      //               "flex" &&
      //             window.getComputedStyle(cartInnerContent).visibility ===
      //               "visible" &&
      //             cartDrawerInner &&
      //             cartDrawerInner.getBoundingClientRect().right > 0 &&
      //             cartDrawerInner.getBoundingClientRect().right <=
      //               window.innerWidth;
      //           if (!isVisible) {
      //             const cartIcon = document.querySelector("#cart-icon-bubble");
      //             if (cartIcon) {
      //               cartIcon.dispatchEvent(
      //                 new Event("click", { bubbles: true })
      //               );
      //             } else {
      //               document.body.classList.remove("overflow-hidden");
      //               const overlay = this.cart.querySelector(
      //                 "#CartDrawer-Overlay"
      //               );
      //               if (overlay) {
      //                 overlay.classList.remove("active");
      //                 overlay.style.display = "none";
      //               }
      //             }
      //           }
      //         }, 500);
      //       };

      //       if (quickAddModal) {
      //         document.body.addEventListener(
      //           "modalClosed",
      //           () => {
      //             setTimeout(() => {
      //               CartPerformance.measure(
      //                 "add:paint-updated-sections",
      //                 renderCartContents
      //               );
      //             });
      //           },
      //           { once: true }
      //         );
      //         quickAddModal.hide(true);
      //       } else {
      //         CartPerformance.measure(
      //           "add:paint-updated-sections",
      //           renderCartContents
      //         );
      //       }
      //     })
      //     .catch((e) => {
      //       document.body.classList.remove("overflow-hidden");
      //     })
      //     .finally(() => {
      //       this.submitButton.classList.remove("loading");
      //       if (this.cart && this.cart.classList.contains("is-empty"))
      //         this.cart.classList.remove("is-empty");
      //       if (!this.error) this.submitButton.removeAttribute("aria-disabled");
      //       this.querySelector(".loading__spinner").classList.add("hidden");

      //       CartPerformance.measureFromEvent("add:user-action", evt);
      //     });
      // }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
